import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTeacher } from '../../context/TeacherContext';
import { useNavigate } from 'react-router-dom';
import apiAxiosInstance from "../../utils/axiosInstance";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VideoHelpButton from '../Common/VideoHelpButton';
import html2pdf from 'html2pdf.js';

interface SavedLessonPlan {
  _id: string;
  form?: {
    standardId: string;
    lexileLevel: string;
    genre: string;
    topic: string;
    paragraphCount: number;
    language?: string;
  };
  standard: {
    code: string;
    description: string;
    _id?: string;
  };
  sections?: {
    introduction?: {
      story?: {
        title: string;
        content: string;
      };
      storySpanish?: {
        title: string;
        content: string;
      };
      activities?: string;
      activitiesSpanish?: string;
      isGenerated?: boolean;
    };
    independent?: {
      story?: {
        title: string;
        content: string;
      };
      storySpanish?: {
        title: string;
        content: string;
      };
      activities?: string;
      activitiesSpanish?: string;
      isGenerated?: boolean;
    };
    warmUp?: string;
    introductionAndGuidedPractice?: string;
    independentPractice?: string;
    checkingComprehension?: string;
  };
  story?: {
    title: string;
    content: string;
  };
  lexileLevel?: string;
  createdAt: string;
  updatedAt?: string;
}

interface RowProps {
  lessonPlan: SavedLessonPlan;
  onDelete: (id: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

const Row: React.FC<RowProps> = ({ lessonPlan, onDelete, error, setError }) => {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleDelete = async (lessonPlanId: string) => {
    setDeleteDialogOpen(true);
  };

  const handleEdit = () => {
    // Navigate to the lesson plan creation page with the lesson data
    navigate('/small-group/lesson-plan', { 
      state: { 
        editingLessonPlan: lessonPlan,
        isEditing: true 
      } 
    });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await apiAxiosInstance.delete(`/api/small-group-lesson-plans/${lessonPlan._id}`);
      onDelete(lessonPlan._id);
      setError(null);
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      setError('Failed to delete lesson plan');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const formatMarkdownContent = (content: string) => {
    return content
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      .replace(/\n\n/g, '\\par\\par ') // Convert double newlines to RTF paragraphs
      .replace(/\n/g, '\\line ') // Convert single newlines to RTF line breaks
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  };

  const formatHtmlContent = (content: string) => {
    if (!content) return '';
    
    // First clean up encoding issues
    let cleanContent = content
      .replace(/&quot;/g, '"') // Fix encoded quotes
      .replace(/&#x27;/g, "'") // Fix encoded apostrophes
      .replace(/&amp;/g, '&') // Fix encoded ampersands
      .replace(/&lt;/g, '<') // Fix encoded less than
      .replace(/&gt;/g, '>') // Fix encoded greater than
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")  // Fix mangled apostrophe
      .replace(/â€œ/g, '"')  // Fix mangled opening quote
      .replace(/â€/g, '"')   // Fix mangled closing quote
      .replace(/â€"/g, '-')  // Fix mangled dash
      .replace(/â€¦/g, '...') // Fix mangled ellipsis
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/–/g, '-')    // En dash to regular dash
      .replace(/—/g, '-')    // Em dash to regular dash
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      .replace(/---+/g, '') // Remove triple dashes and more
      .replace(/#{1,6}\s*/g, ''); // Remove markdown heading markers
    
    // Process line by line to maintain structure like in the UI
    const lines = cleanContent.split('\n').filter(line => line.trim());
    let formattedContent = '';
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (!line) continue;

      // Detect GitHub-Flavored Markdown tables and convert to HTML table
      if (/^\|.*\|$/.test(line) && i + 1 < lines.length && /^\|?\s*(:?-+\s*:?)\s*(\|\s*:?-+\s*:?\s*)+\|?$/.test(lines[i + 1].trim())) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }

        const headerCells = line.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
        const alignSpec = lines[i + 1].trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
        i += 2; // move past header and separator
        let bodyRows: string[] = [];
        while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
          bodyRows.push(lines[i].trim());
          i += 1;
        }
        // step back one since for-loop will increment
        i -= 1;

        const getAlign = (spec: string) => {
          const starts = spec.startsWith(':');
          const ends = spec.endsWith(':');
          if (starts && ends) return 'center';
          if (ends) return 'right';
          if (starts) return 'left';
          return 'left';
        };

        const thead = `<thead><tr>${headerCells.map((h, idx) => `<th style="text-align:${getAlign(alignSpec[idx] || '')}">${h}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${bodyRows.map(row => {
          const cells = row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
          return `<tr>${cells.map((c, idx) => `<td style="text-align:${getAlign(alignSpec[idx] || '')}">${c}</td>`).join('')}</tr>`;
        }).join('')}</tbody>`;

        formattedContent += `<table class="ta-table" style="width:100%; border-collapse:collapse; margin: 12px 0;">${thead}${tbody}</table>`;
        continue;
      }
      
      // Check for section headers with time (like "Pre-Reading Activities (10-15 minutes)")
      if (line.match(/.*\(\d+-\d+\s*minutes?\)/) && !line.startsWith('-') && !line.match(/^\d+\./)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const timeMatch = line.match(/\((\d+-\d+\s*minutes?)\)/);
        const sectionTitle = line.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        formattedContent += `<h3 style="color: #1976d2; font-weight: bold; font-size: 16px; margin: 20px 0 10px 0; border-bottom: 2px solid #1976d2; padding-bottom: 4px;">${sectionTitle}</h3>`;
        if (timeMatch) {
          formattedContent += `<p style="color: #666; font-style: italic; margin: 5px 0 15px 0;">(${timeMatch[1]})</p>`;
        }
        continue;
      }
      
      // Check for numbered activity headers (like "1. Pre-Reading Discussion (2-3 minutes)")
      if (line.match(/^\d+\.\s+/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const activityTitle = line.replace(/^\d+\.\s+/, '');
        const timeMatch = activityTitle.match(/\((\d+-\d+\s*minutes?)\)/);
        const title = activityTitle.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        const numberMatch = line.match(/^\d+\./);
        
        formattedContent += `<h4 style="color: #495057; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 4px 0; border-bottom: 1px solid #dee2e6;">${numberMatch ? numberMatch[0] : ''} ${title}</h4>`;
        if (timeMatch) {
          formattedContent += `<p style="color: #666; font-style: italic; margin: 5px 0 10px 0;">(${timeMatch[1]})</p>`;
        }
        continue;
      }
      
      // Check for special instruction types
      if (line.match(/^([A-Z][^:]*Instructions):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Instructions):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #fff3cd; color: #856404; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #ffc107; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      if (line.match(/^([A-Z][^:]*Expected Responses?):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Expected Responses?):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #d1ecf1; color: #0c5460; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #17a2b8; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      if (line.match(/^([A-Z][^:]*Vocabulary Words?):\s*/)) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        const match = line.match(/^([A-Z][^:]*Vocabulary Words?):\s*(.*)/);
        if (match) {
          formattedContent += `<h4 style="background-color: #d4edda; color: #155724; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 8px 12px; border-left: 4px solid #28a745; border-radius: 4px;">${match[1]}:</h4>`;
          if (match[2]) {
            formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${match[2]}</p>`;
          }
        }
        continue;
      }
      
      // Check for bullet points (this is the key fix!)
      if (line.startsWith('- ')) {
        const bulletText = line.substring(2).trim();
        
        if (!inList) {
          formattedContent += '<ul style="margin: 10px 0; padding-left: 20px;">';
          inList = true;
        }
        
        // Check if it has a label (like "Words to explore:")
        const colonIndex = bulletText.indexOf(':');
        if (colonIndex > 0 && colonIndex < 50) {
          const label = bulletText.substring(0, colonIndex).trim();
          const text = bulletText.substring(colonIndex + 1).trim();
          formattedContent += `<li style="margin-bottom: 8px; line-height: 1.6;"><strong>${label}:</strong> ${text}</li>`;
        } else {
          formattedContent += `<li style="margin-bottom: 8px; line-height: 1.6;">${bulletText}</li>`;
        }
        continue;
      }
      
      // Check for standalone labels (ends with colon)
      if (line.endsWith(':') && line.length < 100) {
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        formattedContent += `<h4 style="color: #495057; font-weight: bold; font-size: 14px; margin: 15px 0 8px 0; padding: 4px 0; border-bottom: 1px solid #dee2e6;">${line}</h4>`;
        continue;
      }
      
      // Regular paragraph text
      if (inList) {
        formattedContent += '</ul>';
        inList = false;
      }
      formattedContent += `<p style="margin-bottom: 12px; line-height: 1.6;">${line}</p>`;
    }
    
    // Close any open list
    if (inList) {
      formattedContent += '</ul>';
    }
    
    return formattedContent;
  };

  // Removes "Título:" or "Title:" prefix from a title string
  const stripTitlePrefix = (title: string): string => {
    if (!title) return '';
    const prefixRegex = /^\s*(?:title|t[íi]tulo)\s*:\s*/i;
    return title.replace(prefixRegex, '').trim();
  };

  // Removes a leading title line from content like "Title: ..." or "Título: ..."
  const stripLeadingTitleLine = (text: string): string => {
    if (!text) return text;
    
    let result = text.trim();
    
    // Multiple regex patterns to catch different formats
    const patterns = [
      // Pattern 1: "Título: ..." or "Title: ..." at start of line
      /^\s*(?:title|t[íi]tulo)\s*:\s*.*$/im,
      // Pattern 2: HTML wrapped version "<p>Título: ...</p>"
      /^\s*<p>\s*(?:title|t[íi]tulo)\s*:\s*.*?<\/p>\s*/im,
      // Pattern 3: Just the title line by itself (no colon)
      /^\s*(?:title|t[íi]tulo)\s*.*$/im,
      // Pattern 4: Any line that starts with "Título:" or "Title:" followed by content
      /^\s*(?:title|t[íi]tulo)\s*:\s*[^\n]*\n?/im
    ];
    
    // Apply each pattern until no more matches
    let changed = true;
    while (changed) {
      changed = false;
      for (const pattern of patterns) {
        if (pattern.test(result)) {
          result = result.replace(pattern, '').trim();
          changed = true;
        }
      }
    }
    
    return result;
  };

  /**
   * Formats story content with proper paragraph structure - preserves original paragraphs
   * Applies genre-specific formatting for poetry, drama, and other content types
   * @param content - The story content to format
   * @param genre - The genre of the content (for special formatting)
   * @returns HTML formatted story content
   */
  const formatStoryContent = (content: string, genre?: string) => {
    if (!content) return '';
    
    // Clean up encoding issues first
    let cleanContent = content
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")  // Fix mangled apostrophe
      .replace(/â€œ/g, '"')  // Fix mangled opening quote
      .replace(/â€/g, '"')   // Fix mangled closing quote
      .replace(/â€"/g, '-')  // Fix mangled dash
      .replace(/â€¦/g, '...') // Fix mangled ellipsis
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/'/g, "'")    // Smart apostrophe to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/"/g, '"')    // Smart quote to regular
      .replace(/–/g, '-')    // En dash to regular dash
      .replace(/—/g, '-');   // Em dash to regular dash

    // Remove duplicated embedded title line if present
    cleanContent = stripLeadingTitleLine(cleanContent);
    
    // Handle Poetry genre with special stanza formatting
    if (genre === 'Poetry') {
      const stanzas = cleanContent
        .split(/\n\s*\n/)
        .filter((stanza: string) => stanza.trim());
      
      return stanzas
        .map((stanza: string, stanzaIndex: number) => {
          const lines = stanza.split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line);
          
          const stanzaHtml = lines
            .map((line: string) => `<div style="text-align: center; margin-bottom: 4px; font-style: italic; line-height: 1.6;">${line}</div>`)
            .join('');
          
          return `<div style="margin-bottom: 24px; page-break-inside: avoid;">${stanzaHtml}</div>`;
        })
        .join('');
    }
    
    // Handle Drama genre with character names and stage directions
    if (genre === 'Drama' && (cleanContent.includes('**') || cleanContent.includes('(Characters):'))) {
      const lines = cleanContent.split('\n').filter((line: string) => line.trim());
      
      return lines
        .map((line: string) => {
          const trimmedLine = line.trim();
          
          // Handle character names in dialogue (bold formatting)
          if (/\*\*[A-Z][^*]*\*\*:/.test(trimmedLine) || /[A-Z][^:]*\*\*:/.test(trimmedLine)) {
            const processedLine = trimmedLine
              .replace(/\*\*([^*]+)\*\*:/g, '<strong>$1</strong>:')
              .replace(/([A-Z][A-Z\s.]+)\*\*:/g, '<strong>$1</strong>:');
            return `<div style="margin-bottom: 8px; line-height: 1.6; page-break-inside: avoid;">${processedLine}</div>`;
          }
          
          // Handle stage directions and other content
          return `<div style="margin-bottom: 8px; line-height: 1.6; page-break-inside: avoid;">${trimmedLine}</div>`;
        })
        .join('');
    }
    
    // Default formatting for other genres
    const paragraphs = cleanContent
      .split(/\n\s*\n/)
      .filter((paragraph: string) => paragraph.trim())
      .map((paragraph: string) => paragraph.trim().replace(/\n/g, ' '));
    
    return paragraphs
      .map((paragraph: string) => `<p style="margin-bottom: 16px; line-height: 1.8; text-indent: 20px; page-break-inside: avoid; orphans: 3; widows: 3;">${paragraph}</p>`)
      .join('');
  };

  const handlePrint = () => {
    // Create hidden iframe to avoid opening a new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const printDoc = iframe.contentWindow?.document;
    if (printDoc) {
      const title = lessonPlan.form?.topic || lessonPlan.story?.title || 'Lesson Plan';
      
      printDoc.open();
      printDoc.write(`
        <html>
          <head>
            <title>Lesson Plan - ${title}</title>
            <style>
              @page {
                margin: 1in;
                size: letter;
              }
              
              @media print {
                @page {
                  margin: 1in;
                  size: letter;
                }
              }

              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }

              h1, h2 {
                color: #1976d2;
                margin-top: 2em;
                margin-bottom: 1em;
              }

              .lesson-info {
                background-color: #f5f5f5;
                padding: 1em;
                margin: 1em 0;
                border-radius: 4px;
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                gap: 1em;
              }

              .story-content {
                padding: 1em;
                margin: 1em 0;
                border-radius: 4px;
                white-space: pre-wrap;
                word-wrap: break-word;
              }

              .section {
                margin-bottom: 2em;
                page-break-inside: avoid;
                break-inside: avoid;
              }

              .activities-content {
                padding: 1em;
                margin: 1em 0;
                background: #fff;
                border-radius: 4px;
              }

              p {
                margin: 1em 0;
                orphans: 4;
                widows: 4;
                page-break-inside: avoid;
                break-inside: avoid;
              }

              /* Enhanced text flow control */
              div {
                orphans: 3;
                widows: 3;
              }

              h1, h2, h3, h4, h5, h6 {
                page-break-after: avoid;
                break-after: avoid;
                orphans: 2;
                widows: 2;
              }

              strong {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              /* List control */
              ul, ol {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              li {
                page-break-inside: avoid;
                break-inside: avoid;
              }
            </style>
          </head>
          <body>
            <h1>Lesson Plan: ${title}</h1>
            
            ${lessonPlan.standard.code ? `
              <div style="margin-bottom: 2em;">
                <strong>Standard:</strong> ${lessonPlan.standard.code}
                ${lessonPlan.standard.description ? `<br><em>${lessonPlan.standard.description}</em>` : ''}
              </div>
            ` : ''}

            ${lessonPlan.form ? `
              <div class="lesson-info">
                <div><strong>Topic:</strong> ${lessonPlan.form.topic}</div>
                <div><strong>Genre:</strong> ${lessonPlan.form.genre}</div>
                <div><strong>Lexile Level:</strong> ${lessonPlan.form.lexileLevel}</div>
                <div><strong>Paragraphs:</strong> ${lessonPlan.form.paragraphCount}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.introduction?.story ? `
              <div class="section">
                <h2>Introduction Story: ${stripTitlePrefix(lessonPlan.sections.introduction.story.title)}</h2>
                <div class="story-content">${formatStoryContent(lessonPlan.sections.introduction.story.content, lessonPlan.form?.genre)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.introduction?.activities ? `
              <div class="section">
                <h2>Introduction Activities</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.introduction.activities)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.introduction?.storySpanish ? `
              <div class="section">
                <h2>Introduction Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.introduction.storySpanish.title)}</h2>
                <div class="story-content">${formatStoryContent(lessonPlan.sections.introduction.storySpanish.content, lessonPlan.form?.genre)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.introduction?.activitiesSpanish ? `
              <div class="section">
                <h2>Introduction Activities (Spanish)</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.introduction.activitiesSpanish)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.independent?.story ? `
              <div class="section">
                <h2>Independent Practice Story: ${stripTitlePrefix(lessonPlan.sections.independent.story.title)}</h2>
                <div class="story-content">${formatStoryContent(lessonPlan.sections.independent.story.content, lessonPlan.form?.genre)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.independent?.activities ? `
              <div class="section">
                <h2>Independent Practice Activities</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.independent.activities)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.independent?.storySpanish ? `
              <div class="section">
                <h2>Independent Practice Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.independent.storySpanish.title)}</h2>
                <div class="story-content">${formatStoryContent(lessonPlan.sections.independent.storySpanish.content, lessonPlan.form?.genre)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.independent?.activitiesSpanish ? `
              <div class="section">
                <h2>Independent Practice Activities (Spanish)</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.independent.activitiesSpanish)}</div>
              </div>
            ` : ''}

            ${lessonPlan.story && !lessonPlan.sections?.introduction && !lessonPlan.sections?.independent ? `
              <div class="section">
                <h2>Story: ${lessonPlan.story.title}</h2>
                <div class="story-content">${lessonPlan.story.content}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.warmUp ? `
              <div class="section">
                <h2>Warm Up</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.warmUp)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.introductionAndGuidedPractice ? `
              <div class="section">
                <h2>Introduction and Guided Practice</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.introductionAndGuidedPractice)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.independentPractice ? `
              <div class="section">
                <h2>Independent Practice</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.independentPractice)}</div>
              </div>
            ` : ''}

            ${lessonPlan.sections?.checkingComprehension ? `
              <div class="section">
                <h2>Checking Comprehension</h2>
                <div class="activities-content">${formatHtmlContent(lessonPlan.sections.checkingComprehension)}</div>
              </div>
            ` : ''}
          </body>
        </html>
      `);
      printDoc.close();

      // Ensure the iframe content is ready before printing
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        // Cleanup iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
    }
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadPDF = async () => {
    handleDownloadClose();
    
    const title = lessonPlan.form?.topic || lessonPlan.story?.title || 'lesson-plan';
    
    const element = document.createElement('div');
    element.innerHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <style>
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Enhanced page break control */
            .section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: block;
              position: relative;
              margin: 20px 0;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background-color: #fafafa;
              min-height: 100px;
            }
            
            h1 {
              font-size: 24px !important;
              font-weight: bold !important;
              color: #1976d2 !important;
              text-align: center !important;
              margin: 0 0 25px 0 !important;
              padding: 10px 0 !important;
              border-bottom: 2px solid #1976d2 !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            h2 {
              page-break-after: avoid !important;
              break-after: avoid !important;
              font-size: 18px !important;
              font-weight: bold !important;
              color: #1976d2 !important;
              margin: 0 0 15px 0 !important;
              padding: 8px 12px !important;
              background-color: #e3f2fd !important;
              border-left: 4px solid #1976d2 !important;
              border-radius: 4px !important;
              orphans: 2;
              widows: 2;
            }
            
            .content-wrapper {
              page-break-before: avoid !important;
              break-before: avoid !important;
              orphans: 3;
              widows: 3;
            }
            
            .content {
              page-break-before: avoid !important;
              break-before: avoid !important;
              white-space: pre-wrap;
              word-wrap: break-word;
              line-height: 1.6;
              orphans: 4;
              widows: 4;
            }
            
            /* Enhanced paragraph control */
            p {
              orphans: 4 !important;
              widows: 4 !important;
              margin: 0 0 12px 0;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              line-height: 1.8 !important;
            }
            
            /* Enhanced div control for poetry and drama */
            div {
              orphans: 3;
              widows: 3;
            }
            
            /* Strong tags for character names */
            strong {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* List control */
            ul, ol {
              orphans: 2;
              widows: 2;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            li {
              orphans: 2;
              widows: 2;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }
        </style>
        <h1 style="color: #1976d2; margin: 0 0 20px 0;">Lesson Plan: ${title}</h1>
        
        ${lessonPlan.standard.code ? `
          <div style="margin: 0 0 20px 0;">
            <strong>Standard:</strong> ${lessonPlan.standard.code}
            ${lessonPlan.standard.description ? `<br><em>${lessonPlan.standard.description}</em>` : ''}
          </div>
        ` : ''}

        ${lessonPlan.form ? `
          <div style="margin: 0 0 20px 0; padding: 15px; border-radius: 4px;">
            <strong>Topic:</strong> ${lessonPlan.form.topic} | 
            <strong>Genre:</strong> ${lessonPlan.form.genre} | 
            <strong>Lexile Level:</strong> ${lessonPlan.form.lexileLevel} | 
            <strong>Paragraphs:</strong> ${lessonPlan.form.paragraphCount}
          </div>
        ` : ''}

        ${lessonPlan.sections?.introduction?.story ? `
          <div class="section">
            <h2 style="color: #1976d2;">Introduction Story: ${stripTitlePrefix(lessonPlan.sections.introduction.story.title)}</h2>
            <div class="content-wrapper">
              <div class="content" style="padding: 15px; border-radius: 4px;">
                ${formatStoryContent(lessonPlan.sections.introduction.story.content, lessonPlan.form?.genre)}
              </div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.introduction?.activities ? `
          <div class="section">
            <h2 style="color: #1976d2;">Introduction Activities</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.introduction.activities)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.introduction?.storySpanish ? `
          <div class="section">
            <h2 style="color: #1976d2;">Introduction Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.introduction.storySpanish.title)}</h2>
            <div class="content-wrapper">
              <div class="content" style="padding: 15px; border-radius: 4px;">
                ${formatStoryContent(lessonPlan.sections.introduction.storySpanish.content, lessonPlan.form?.genre)}
              </div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.introduction?.activitiesSpanish ? `
          <div class="section">
            <h2 style="color: #1976d2;">Introduction Activities (Spanish)</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.introduction.activitiesSpanish)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.independent?.story ? `
          <div class="section">
            <h2 style="color: #1976d2;">Independent Practice Story: ${stripTitlePrefix(lessonPlan.sections.independent.story.title)}</h2>
            <div class="content-wrapper">
              <div class="content" style="padding: 15px; border-radius: 4px;">
                ${formatStoryContent(lessonPlan.sections.independent.story.content, lessonPlan.form?.genre)}
              </div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.independent?.activities ? `
          <div class="section">
            <h2 style="color: #1976d2;">Independent Practice Activities</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.independent.activities)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.independent?.storySpanish ? `
          <div class="section">
            <h2 style="color: #1976d2;">Independent Practice Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.independent.storySpanish.title)}</h2>
            <div class="content-wrapper">
              <div class="content" style="padding: 15px; border-radius: 4px;">
                ${formatStoryContent(lessonPlan.sections.independent.storySpanish.content, lessonPlan.form?.genre)}
              </div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.independent?.activitiesSpanish ? `
          <div class="section">
            <h2 style="color: #1976d2;">Independent Practice Activities (Spanish)</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.independent.activitiesSpanish)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.story && !lessonPlan.sections?.introduction && !lessonPlan.sections?.independent ? `
          <div class="section">
            <h2 style="color: #1976d2;">Story: ${lessonPlan.story.title}</h2>
            <div class="content-wrapper">
              <div class="content" style="padding: 15px; border-radius: 4px;">
                ${lessonPlan.story.content}
              </div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.warmUp ? `
          <div class="section">
            <h2 style="color: #1976d2;">Warm Up</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.warmUp)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.introductionAndGuidedPractice ? `
          <div class="section">
            <h2 style="color: #1976d2;">Introduction and Guided Practice</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.introductionAndGuidedPractice)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.independentPractice ? `
          <div class="section">
            <h2 style="color: #1976d2;">Independent Practice</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.independentPractice)}</div>
            </div>
          </div>
        ` : ''}

        ${lessonPlan.sections?.checkingComprehension ? `
          <div class="section">
            <h2 style="color: #1976d2;">Checking Comprehension</h2>
            <div class="content-wrapper">
              <div class="content">${formatHtmlContent(lessonPlan.sections.checkingComprehension)}</div>
            </div>
          </div>
        ` : ''}
      </div>
    `;

    const opt = {
      margin: 0.75,
      filename: `lesson-plan-${title}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all'],
        before: '.section',
        avoid: ['h2', '.content-wrapper', '.content']
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadWord = () => {
    handleDownloadClose();
    
    const title = lessonPlan.form?.topic || lessonPlan.story?.title || 'lesson-plan';
    
    let rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0\\fnil\\fcharset0 Arial;}}
{\\colortbl;\\red25\\green118\\blue210;}

\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440
\\widowctrl\\ftnbj\\aenddoc\\wrapdefault\\adjustright

\\pard\\qc\\b\\fs36 Lesson Plan: ${title}\\par
${lessonPlan.standard.code ? `\\pard\\sa360\\fs24\\b Standard: ${lessonPlan.standard.code}\\par` : ''}
${lessonPlan.standard.description ? `\\pard\\sa360\\fs24\\i ${lessonPlan.standard.description}\\i0\\par` : ''}

${lessonPlan.form ? `
\\pard\\sa360\\fs24\\b Topic: ${lessonPlan.form.topic}\\par
\\pard\\sa360\\fs24\\b Genre: ${lessonPlan.form.genre}\\par
\\pard\\sa360\\fs24\\b Lexile Level: ${lessonPlan.form.lexileLevel}\\par
\\pard\\sa360\\fs24\\b Paragraphs: ${lessonPlan.form.paragraphCount}\\par
` : ''}

${lessonPlan.sections?.introduction?.story ? `
\\pard\\qc\\b\\fs32 Introduction Story: ${stripTitlePrefix(lessonPlan.sections.introduction.story.title)}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.sections.introduction.story.content}\\par
` : ''}

${lessonPlan.sections?.introduction?.activities ? `
\\pard\\qc\\b\\fs32 Introduction Activities\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.introduction.activities)}\\par
` : ''}

${lessonPlan.sections?.introduction?.storySpanish ? `
\\pard\\qc\\b\\fs32 Introduction Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.introduction.storySpanish.title)}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.sections.introduction.storySpanish.content}\\par
` : ''}

${lessonPlan.sections?.introduction?.activitiesSpanish ? `
\\pard\\qc\\b\\fs32 Introduction Activities (Spanish)\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.introduction.activitiesSpanish)}\\par
` : ''}

${lessonPlan.sections?.independent?.story ? `
\\pard\\qc\\b\\fs32 Independent Practice Story: ${stripTitlePrefix(lessonPlan.sections.independent.story.title)}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.sections.independent.story.content}\\par
` : ''}

${lessonPlan.sections?.independent?.activities ? `
\\pard\\qc\\b\\fs32 Independent Practice Activities\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.independent.activities)}\\par
` : ''}

${lessonPlan.sections?.independent?.storySpanish ? `
\\pard\\qc\\b\\fs32 Independent Practice Story (Spanish): ${stripTitlePrefix(lessonPlan.sections.independent.storySpanish.title)}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.sections.independent.storySpanish.content}\\par
` : ''}

${lessonPlan.sections?.independent?.activitiesSpanish ? `
\\pard\\qc\\b\\fs32 Independent Practice Activities (Spanish)\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.independent.activitiesSpanish)}\\par
` : ''}

${lessonPlan.story && !lessonPlan.sections?.introduction && !lessonPlan.sections?.independent ? `
\\pard\\qc\\b\\fs32 Story: ${lessonPlan.story.title}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.story.content}\\par
` : ''}

${lessonPlan.sections?.warmUp ? `
\\pard\\qc\\b\\fs32 Warm Up\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.warmUp)}\\par
` : ''}

${lessonPlan.sections?.introductionAndGuidedPractice ? `
\\pard\\qc\\b\\fs32 Introduction and Guided Practice\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.introductionAndGuidedPractice)}\\par
` : ''}

${lessonPlan.sections?.independentPractice ? `
\\pard\\qc\\b\\fs32 Independent Practice\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.independentPractice)}\\par
` : ''}

${lessonPlan.sections?.checkingComprehension ? `
\\pard\\qc\\b\\fs32 Checking Comprehension\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.checkingComprehension)}\\par
` : ''}
}`;

    const wordBlob = new Blob([rtfContent], { type: 'application/msword' });
    const wordLink = document.createElement('a');
    wordLink.href = URL.createObjectURL(wordBlob);
    wordLink.download = `lesson-plan-${title}.doc`;
    document.body.appendChild(wordLink);
    wordLink.click();
    document.body.removeChild(wordLink);
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {/* Show topic for new format, story title for legacy */}
          {lessonPlan.form?.topic || lessonPlan.story?.title || 'No title'}
          {lessonPlan.form?.genre && (
            <Typography variant="caption" display="block" color="text.secondary">
              {lessonPlan.form.genre}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
            {lessonPlan.standard.code}
          </Typography>
          <Typography variant="body2">
            {lessonPlan.standard.description}
          </Typography>
        </TableCell>
        <TableCell>{lessonPlan.form?.lexileLevel || lessonPlan.lexileLevel || 'N/A'}</TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(lessonPlan._id);
            }}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div" color="primary">
                Lesson Plan Details
              </Typography>
              
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  sx={{
                    background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #e65100 30%, #f57c00 90%)',
                    }
                  }}
                >
                  Edit
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrint();
                  }}
                >
                  Print
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadClick(e);
                  }}
                >
                  Download
                </Button>
              </Box>
              
              <Grid container spacing={3}>
                {/* Lesson Info */}
                {lessonPlan.form && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Lesson Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Topic:</Typography>
                          <Typography variant="body1" fontWeight={600}>{lessonPlan.form.topic}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Genre:</Typography>
                          <Typography variant="body1">{lessonPlan.form.genre}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Lexile Level:</Typography>
                          <Typography variant="body1">{lessonPlan.form.lexileLevel}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">Paragraphs:</Typography>
                          <Typography variant="body1">{lessonPlan.form.paragraphCount}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* New Format: Introduction Section */}
                {lessonPlan.sections?.introduction && (
                  <>
                    {lessonPlan.sections.introduction.story && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Introduction Story: {lessonPlan.sections.introduction.story.title}
                          </Typography>
                          <Box sx={{ 
                            maxHeight: '200px', 
                            overflow: 'auto',
                            backgroundColor: 'grey.50',
                            p: 2,
                            borderRadius: 1,
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'grey.100',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'grey.400',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'grey.500',
                              },
                            },
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.introduction.story.content}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    
                    {lessonPlan.sections.introduction.activities && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Introduction Activities
                          </Typography>
                          <Box sx={{ 
                            backgroundColor: 'grey.50',
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              color: 'primary.main',
                              fontWeight: 600,
                              marginTop: 2,
                              marginBottom: 1,
                            },
                            '& h1': { fontSize: '1.5rem' },
                            '& h2': { fontSize: '1.3rem' },
                            '& h3': { fontSize: '1.1rem' },
                            '& p': {
                              marginBottom: 1.5,
                              lineHeight: 1.7,
                              fontSize: '0.95rem',
                            },
                            '& ul, & ol': {
                              paddingLeft: 3,
                              marginBottom: 2,
                              '& li': {
                                marginBottom: 0.5,
                                lineHeight: 1.6,
                              }
                            },
                            '& strong': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& em': {
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            },
                            '& blockquote': {
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              paddingLeft: 2,
                              marginLeft: 1,
                              marginRight: 0,
                              fontStyle: 'italic',
                              backgroundColor: 'background.paper',
                              padding: 2,
                              borderRadius: 1,
                            }
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.introduction.activities}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Spanish Translation for Introduction */}
                    {lessonPlan.sections.introduction.storySpanish && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Introduction Story (Spanish): {lessonPlan.sections.introduction.storySpanish.title}
                          </Typography>
                          <Box sx={{ 
                            maxHeight: '200px', 
                            overflow: 'auto',
                            backgroundColor: 'grey.50',
                            p: 2,
                            borderRadius: 1,
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'grey.100',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'grey.400',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'grey.500',
                              },
                            },
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.introduction.storySpanish.content}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    
                    {lessonPlan.sections.introduction.activitiesSpanish && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Introduction Activities (Spanish)
                          </Typography>
                          <Box sx={{ 
                            backgroundColor: 'grey.50',
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              color: 'primary.main',
                              fontWeight: 600,
                              marginTop: 2,
                              marginBottom: 1,
                            },
                            '& h1': { fontSize: '1.5rem' },
                            '& h2': { fontSize: '1.3rem' },
                            '& h3': { fontSize: '1.1rem' },
                            '& p': {
                              marginBottom: 1.5,
                              lineHeight: 1.7,
                              fontSize: '0.95rem',
                            },
                            '& ul, & ol': {
                              paddingLeft: 3,
                              marginBottom: 2,
                              '& li': {
                                marginBottom: 0.5,
                                lineHeight: 1.6,
                              }
                            },
                            '& strong': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& em': {
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            },
                            '& blockquote': {
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              paddingLeft: 2,
                              marginLeft: 1,
                              marginRight: 0,
                              fontStyle: 'italic',
                              backgroundColor: 'background.paper',
                              padding: 2,
                              borderRadius: 1,
                            }
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.introduction.activitiesSpanish}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

                {/* New Format: Independent Section */}
                {lessonPlan.sections?.independent && (
                  <>
                    {lessonPlan.sections.independent.story && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Independent Practice Story: {lessonPlan.sections.independent.story.title}
                          </Typography>
                          <Box sx={{ 
                            maxHeight: '200px', 
                            overflow: 'auto',
                            backgroundColor: 'grey.50',
                            p: 2,
                            borderRadius: 1,
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'grey.100',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'grey.400',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'grey.500',
                              },
                            },
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.independent.story.content}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    
                    {lessonPlan.sections.independent.activities && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Independent Practice Activities
                          </Typography>
                          <Box sx={{ 
                            backgroundColor: 'grey.50',
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              color: 'primary.main',
                              fontWeight: 600,
                              marginTop: 2,
                              marginBottom: 1,
                            },
                            '& h1': { fontSize: '1.5rem' },
                            '& h2': { fontSize: '1.3rem' },
                            '& h3': { fontSize: '1.1rem' },
                            '& p': {
                              marginBottom: 1.5,
                              lineHeight: 1.7,
                              fontSize: '0.95rem',
                            },
                            '& ul, & ol': {
                              paddingLeft: 3,
                              marginBottom: 2,
                              '& li': {
                                marginBottom: 0.5,
                                lineHeight: 1.6,
                              }
                            },
                            '& strong': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& em': {
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            },
                            '& blockquote': {
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              paddingLeft: 2,
                              marginLeft: 1,
                              marginRight: 0,
                              fontStyle: 'italic',
                              backgroundColor: 'background.paper',
                              padding: 2,
                              borderRadius: 1,
                            }
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.independent.activities}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Spanish Translation for Independent Practice */}
                    {lessonPlan.sections.independent.storySpanish && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Independent Practice Story (Spanish): {lessonPlan.sections.independent.storySpanish.title}
                          </Typography>
                          <Box sx={{ 
                            maxHeight: '200px', 
                            overflow: 'auto',
                            backgroundColor: 'grey.50',
                            p: 2,
                            borderRadius: 1,
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'grey.100',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'grey.400',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'grey.500',
                              },
                            },
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.independent.storySpanish.content}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    
                    {lessonPlan.sections.independent.activitiesSpanish && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            Independent Practice Activities (Spanish)
                          </Typography>
                          <Box sx={{ 
                            backgroundColor: 'grey.50',
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              color: 'primary.main',
                              fontWeight: 600,
                              marginTop: 2,
                              marginBottom: 1,
                            },
                            '& h1': { fontSize: '1.5rem' },
                            '& h2': { fontSize: '1.3rem' },
                            '& h3': { fontSize: '1.1rem' },
                            '& p': {
                              marginBottom: 1.5,
                              lineHeight: 1.7,
                              fontSize: '0.95rem',
                            },
                            '& ul, & ol': {
                              paddingLeft: 3,
                              marginBottom: 2,
                              '& li': {
                                marginBottom: 0.5,
                                lineHeight: 1.6,
                              }
                            },
                            '& strong': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& em': {
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            },
                            '& blockquote': {
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              paddingLeft: 2,
                              marginLeft: 1,
                              marginRight: 0,
                              fontStyle: 'italic',
                              backgroundColor: 'background.paper',
                              padding: 2,
                              borderRadius: 1,
                            }
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.independent.activitiesSpanish}</ReactMarkdown>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

                {/* Legacy Format: Single Story */}
                {lessonPlan.story && !lessonPlan.sections?.introduction && !lessonPlan.sections?.independent && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Story: {lessonPlan.story.title}
                      </Typography>
                      <Box sx={{ 
                        maxHeight: '200px', 
                        overflow: 'auto',
                        backgroundColor: 'grey.50',
                        p: 2,
                        borderRadius: 1,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'grey.100',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'grey.400',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'grey.500',
                          },
                        },
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.story.content}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Legacy Format: Old Sections */}
                {lessonPlan.sections?.warmUp && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Warm Up
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: 'grey.50',
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'primary.main',
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': {
                          marginBottom: 1.5,
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        },
                        '& ul, & ol': {
                          paddingLeft: 3,
                          marginBottom: 2,
                          '& li': {
                            marginBottom: 0.5,
                            lineHeight: 1.6,
                          }
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: 'text.primary',
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        },
                        '& blockquote': {
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                          paddingLeft: 2,
                          marginLeft: 1,
                          marginRight: 0,
                          fontStyle: 'italic',
                          backgroundColor: 'background.paper',
                          padding: 2,
                          borderRadius: 1,
                        }
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.warmUp}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {lessonPlan.sections?.introductionAndGuidedPractice && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Introduction and Guided Practice
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: 'grey.50',
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'primary.main',
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': {
                          marginBottom: 1.5,
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        },
                        '& ul, & ol': {
                          paddingLeft: 3,
                          marginBottom: 2,
                          '& li': {
                            marginBottom: 0.5,
                            lineHeight: 1.6,
                          }
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: 'text.primary',
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        },
                        '& blockquote': {
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                          paddingLeft: 2,
                          marginLeft: 1,
                          marginRight: 0,
                          fontStyle: 'italic',
                          backgroundColor: 'background.paper',
                          padding: 2,
                          borderRadius: 1,
                        }
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.introductionAndGuidedPractice}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {lessonPlan.sections?.independentPractice && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Independent Practice
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: 'grey.50',
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'primary.main',
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': {
                          marginBottom: 1.5,
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        },
                        '& ul, & ol': {
                          paddingLeft: 3,
                          marginBottom: 2,
                          '& li': {
                            marginBottom: 0.5,
                            lineHeight: 1.6,
                          }
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: 'text.primary',
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        },
                        '& blockquote': {
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                          paddingLeft: 2,
                          marginLeft: 1,
                          marginRight: 0,
                          fontStyle: 'italic',
                          backgroundColor: 'background.paper',
                          padding: 2,
                          borderRadius: 1,
                        }
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.independentPractice}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {lessonPlan.sections?.checkingComprehension && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Checking Comprehension
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: 'grey.50',
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'primary.main',
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': {
                          marginBottom: 1.5,
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        },
                        '& ul, & ol': {
                          paddingLeft: 3,
                          marginBottom: 2,
                          '& li': {
                            marginBottom: 0.5,
                            lineHeight: 1.6,
                          }
                        },
                        '& strong': {
                          fontWeight: 600,
                          color: 'text.primary',
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        },
                        '& blockquote': {
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                          paddingLeft: 2,
                          marginLeft: 1,
                          marginRight: 0,
                          fontStyle: 'italic',
                          backgroundColor: 'background.paper',
                          padding: 2,
                          borderRadius: 1,
                        }
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessonPlan.sections.checkingComprehension}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Lesson Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lesson plan? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDownloadClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MuiMenuItem onClick={handleDownloadPDF}>
          <PictureAsPdfIcon sx={{ mr: 1 }} />
          PDF
        </MuiMenuItem>
        <MuiMenuItem onClick={handleDownloadWord}>
          <DescriptionIcon sx={{ mr: 1 }} />
          Word
        </MuiMenuItem>
      </Menu>
    </>
  );
};

const SavedLessonPlans: React.FC = () => {
  const { teacher } = useTeacher();
  const [lessonPlans, setLessonPlans] = useState<SavedLessonPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLessonPlans = async () => {
      try {
        setLoading(true);
        const response = await apiAxiosInstance.get('/api/small-group-lesson-plans');
        setLessonPlans(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching lesson plans:', error);
        setError('Failed to load lesson plans');
      } finally {
        setLoading(false);
      }
    };

    if (teacher?._id) {
      fetchLessonPlans();
    }
  }, [teacher]);

  const handleDelete = (deletedId: string) => {
    setLessonPlans(prev => prev.filter(plan => plan._id !== deletedId));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Saved Lesson Plans
        </Typography>
        <VideoHelpButton
          url="https://www.youtube.com/watch?v=aLcIsySHBrY"
          title="Saved Lesson Plans"
          buttonLabel="Watch tutorial"
          color="secondary"
          emphasis="solid"
        />
      </Box>
      

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Topic / Title</TableCell>
              <TableCell>Standard</TableCell>
              <TableCell>Lexile Level</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lessonPlans.map((lessonPlan) => (
              <Row 
                key={lessonPlan._id} 
                lessonPlan={lessonPlan} 
                onDelete={handleDelete}
                error={error}
                setError={setError}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SavedLessonPlans; 